import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { ACTIVE_USER_TOKEN, container } from '@/container';
import { getDisplayMode } from '@/hooks/use-user-platform';
import type { AuthType } from '@/models/auth';
import { createStorageByUserId, removeStorages, removeStoragesByUserId } from '@/services/storage-manager';
import { AUTH_STORAGE_KEY, USER_SWITCH_SELECTED_STORAGE_KEY } from '@/utils/constants';

interface AuthState {
  isHydrated: boolean;
  isUserSelected: boolean;

  credentials: Map<number, AuthType>;

  auth: AuthType | null;
  authError: string | false | null;
}

interface AuthActions {
  setHydrated(hasHydrated: boolean): void;

  start(session: AuthType, isUserSelected?: boolean): void;

  stop(reason?: string | false, userId?: number): void;

  stopAll(reason?: string | false): void;

  updateAuth(auth: Partial<AuthType>): void;

  updateToken(token: string, userId?: number): void;

  updateTraceRate(traceRate: number, userId?: number): void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    immer(
      (set, get): AuthStore => ({
        isHydrated: false,
        isUserSelected: false,
        credentials: new Map<number, AuthType>(),
        auth: null,
        authError: null,

        setHydrated(hasHydrated) {
          set((draft) => {
            draft.isHydrated = hasHydrated;
          });
        },

        start(auth, isUserSelected = true) {
          set((draft) => {
            draft.credentials.set(auth.UserID, auth);

            if (isUserSelected) {
              sessionStorage.setItem(USER_SWITCH_SELECTED_STORAGE_KEY, auth.UserID.toString());
            } else {
              sessionStorage.removeItem(USER_SWITCH_SELECTED_STORAGE_KEY);
            }

            draft.isUserSelected = isUserSelected;
            draft.auth = auth;
            draft.authError = null;
            container.bindValue(ACTIVE_USER_TOKEN, auth.UserID);
          });
        },
        stop(reason, userId) {
          const currentUserId = userId ?? get().auth?.UserID;
          if (!currentUserId) return;

          setTimeout(() => removeStoragesByUserId(currentUserId), 3000);

          set((draft) => {
            const cred = draft.credentials.get(currentUserId);

            if (cred) {
              if (reason !== undefined) {
                cred.Error = reason;
              } else {
                draft.credentials.delete(currentUserId);
              }
            }

            if (draft.auth?.UserID === currentUserId) {
              container.remove(ACTIVE_USER_TOKEN);
              draft.isUserSelected = false;
              draft.auth = null;
              draft.authError = reason ?? null;
            }
          });
        },
        stopAll(reason?: string | false) {
          setTimeout(() => {
            removeStorages();
          }, 3000);
          container.remove(ACTIVE_USER_TOKEN);

          set((draft) => {
            if (reason === undefined) {
              draft.credentials.clear();
            } else {
              for (const cred of draft.credentials.values()) {
                cred.Error = reason;
              }
            }

            draft.isUserSelected = false;
            draft.auth = null;
            draft.authError = null;
          });
        },
        updateToken(token, userId) {
          const currentUserId = userId ?? get().auth?.UserID;
          if (!currentUserId) return;

          set((draft) => {
            const cred = draft.credentials.get(currentUserId);

            if (cred) {
              cred.Token = token;
            }

            if (draft.auth?.UserID === currentUserId) {
              draft.auth.Token = token;
            }
          });
        },
        updateAuth(auth) {
          const currentUserId = auth.UserID ?? get().auth?.UserID;
          if (!currentUserId) return;

          set((draft) => {
            const cred = draft.credentials.get(currentUserId);

            if (cred) {
              if (auth.NickName) cred.NickName = auth.NickName;
              if (auth.AvatarUrl) cred.AvatarUrl = auth.AvatarUrl;
            }

            if (draft.auth?.UserID === currentUserId) {
              if (auth.NickName) draft.auth.NickName = auth.NickName;
              if (auth.AvatarUrl) draft.auth.AvatarUrl = auth.AvatarUrl;
            }
          });
        },
        updateTraceRate(traceRate, userId) {
          const currentUserId = userId ?? get().auth?.UserID;
          if (!currentUserId) return;

          set((draft) => {
            const cred = draft.credentials.get(currentUserId);
            if (cred) {
              cred.TraceRate = traceRate;
            }

            if (draft.auth?.UserID === currentUserId) {
              draft.auth.TraceRate = traceRate;
            }
          });
        },
      }),
    ),
    {
      name: AUTH_STORAGE_KEY,
      version: 4,
      storage: createStorageByUserId('LOCAL_STORAGE', 0),
      partialize: (state) => ({
        credentials: state.credentials,
      }),
      onRehydrateStorage: (prevState) => (state) => {
        if (state) {
          if (prevState.auth) {
            state.start(prevState.auth, prevState.isUserSelected);
          } else {
            let auth: AuthType | undefined;
            if (getDisplayMode() === 'desktop') {
              const userSelected = Number(sessionStorage.getItem(USER_SWITCH_SELECTED_STORAGE_KEY));
              if (userSelected) {
                auth = state.credentials.get(userSelected);
              }
            } else {
              auth = state.credentials.values().next().value;
            }

            if (auth) {
              state.start(auth, true);
            }
          }

          state.setHydrated(true);
        }
      },
      migrate: () => ({
        credentials: new Map(),
      }),
    },
  ),
);

export function getActiveUserId() {
  return container.get(ACTIVE_USER_TOKEN);
}

export function getUserId() {
  return useAuthStore.getState().auth?.UserID;
}

export function useUserId() {
  return useAuthStore((state) => state.auth?.UserID);
}

export function useIsAuthenticated() {
  return useAuthStore((state) => !!state.auth);
}

export function useHasMultiSessions() {
  return useAuthStore((state) => state.credentials.size > 1);
}

export function ensureAuthStoreRehydrated(): Promise<AuthStore> {
  return new Promise((resolve) => {
    const store = useAuthStore.getState();
    if (store.isHydrated) return resolve(store);
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.isHydrated) {
        resolve(state);
        unsubscribe();
      }
    });
  });
}

export async function getAuth(): Promise<AuthType | null> {
  const store = await ensureAuthStoreRehydrated();

  return store.auth;
}
